import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import dayjs from 'dayjs';
import AddToBagButton from 'components/AddToBagButton';
import EffectBadge from 'components/EffectBadge';
import FavoriteProductButton from 'components/FavoriteProductButton';
import Image from 'components/Image';
import Link from 'components/Link';
import LumeTooltip from 'components/LumeTooltip';
import PillBadge from 'components/PillBadge';
import ProductDiscountsInfo from 'components/ProductDiscountsInfo';
import { ADD_TO_CART, ADD_TO_CART_UPSELL } from 'constants/gtm';
import {
  transformLabResultUnit,
  transformVariation,
} from 'data/dutchie/transformations';
import useElementOnScreenCallback from 'hooks/useElementOnScreenCallback';
import { addFavorite, removeFavorite } from 'stores/actions/favorite-products';
import { selectProductPromotions } from 'stores/selectors/product-promo';
import codify from 'utils/codify';
import getSkuSuffix from 'utils/get-sku-suffix';
import percentOff from 'utils/percent-off';
import toDollars from 'utils/to-dollars';
import styles from './ShopCard.module.scss';

const thcCbdTerpsText = (value, prefix, suffix) =>
  Number.parseFloat(value) ? `${prefix}${value}${suffix}` : undefined;

const APPRAREL_SIZES = new Set([
  '2XS',
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '2XL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
  '7XL',
  '8XL',
]);

const FAVORITE_ERROR_MESSAGE = 'Please login to save favorite products.';

/* eslint-disable-next-line complexity */
export default function ShopCard({
  availableDiscounts = [],
  availableQuantity = 0,
  backInStockText,
  badgeImages = [],
  batchSKU,
  brand,
  cartQuantity = 0,
  category,
  closeoutText,
  currencyCode,
  customCss = '',
  discountedCents,
  displayTitle,
  displayVariation,
  effect,
  errorFallbackImage,
  featuredProductText,
  groupedDiscountsContent,
  handleObserved,
  inventoryStatus,
  isAbbreviatedCard = false,
  isUpsell = false,
  mgCBD,
  mgTHC,
  newProductText,
  outOfStock = false,
  packagedDate,
  packagedDateTooltipInfo,
  packageSize,
  percentCBD,
  percentTHC,
  placeholder,
  priceCents,
  packagedDateConfiguration = {},
  priceDropText,
  primaryImageUrl,
  productCode,
  SKU,
  selectedMenu,
  storeSelection,
  store,
  strain,
  tags,
  terpeneTooltipInfo,
  totalTerpenes,
  totalTerpenesUnit,
  url,
  weight,
  weightUOM,
  variation,
}) {
  const { productPromoBySku = {}, productPromoByCategory = {} } = useSelector(
    (state) => selectProductPromotions(state)
  );
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const favoriteSkus = useSelector((state) => state.favoriteProducts);
  const showPackagedDateFlag = useSelector(
    (state) => state.featureFlags.packaged_date
  );
  const selectedStoreData = useSelector(
    (state) => state.selectedStoreInformation
  );
  const showTerpeneInfo = useSelector(
    (state) => state.featureFlags.terpene_info
  );

  const [showFavoriteError, setShowFavoriteError] = useState(false);
  const [storeClosed, setStoreClosed] = useState(false);

  useEffect(() => {
    const selectedStoreClosed = selectedStoreData?.store_closed ?? false;
    if (selectedStoreClosed !== storeClosed) {
      setStoreClosed(selectedStoreClosed);
    }
  }, [selectedStoreData]);

  const handleToggleFavorite = useCallback(() => {
    if (!isLoggedIn) {
      setShowFavoriteError(true);
      return;
    }

    if (favoriteSkus?.includes(SKU)) {
      dispatch(removeFavorite({ SKU }));
    } else {
      dispatch(addFavorite({ SKU }));
    }
  }, [isLoggedIn, dispatch, SKU, favoriteSkus]);

  const cardRef = useElementOnScreenCallback(handleObserved);

  const {
    label: packagedDateLabel,
    max_age_days: packagedDateMaxDays,
    categories: packagedDateCategories = [],
  } = packagedDateConfiguration;

  const showPackagedDate =
    showPackagedDateFlag &&
    packagedDateLabel &&
    packagedDate &&
    (!packagedDateMaxDays ||
      dayjs(packagedDate).add(packagedDateMaxDays, 'day') >= dayjs()) &&
    packagedDateCategories.find((x) => x === category);
  const formattedPackagedDate = packagedDate
    ? dayjs(packagedDate).format('M/D/YY')
    : undefined;

  const promo =
    productPromoBySku[SKU] || productPromoByCategory[category?.toLowerCase()];
  const displayCents = discountedCents || priceCents;
  const price = toDollars(displayCents).toFixed(2);
  const [dollars, cents] = price.split('.');
  const fullPrice =
    priceCents !== displayCents && toDollars(priceCents).toFixed(2);
  const [fullDollars, fullCents] = fullPrice ? fullPrice.split('.') : [];
  const thcCbdTerps = [
    thcCbdTerpsText(percentTHC, 'THC: ', '%'),
    thcCbdTerpsText(mgTHC, 'THC: ', 'mg'),
    showTerpeneInfo
      ? thcCbdTerpsText(
          totalTerpenes,
          'Terps: ',
          transformLabResultUnit(totalTerpenesUnit)
        )
      : undefined,
    thcCbdTerpsText(percentCBD, 'CBD: ', '%'),
    thcCbdTerpsText(mgCBD, 'CBD: ', 'mg'),
  ]
    .filter(Boolean)
    .slice(0, 2)
    .join(' | ');
  const discountPercent = percentOff(priceCents, discountedCents);
  const showDiscount = discountPercent > 0;
  const badgeImage =
    typeof effect === 'string'
      ? badgeImages.find(
          (badge) => badge.title === `badge-${codify(effect)}.svg`
        )
      : null;

  const variantSkuQuery = productCode ? `?variant_sku=${productCode}` : '';
  const productUrl = url ? `${url}${variantSkuQuery}` : null;

  const itemSuffix = getSkuSuffix(category, selectedMenu);

  const usePackageSize =
    packageSize &&
    (Number.isFinite(packageSize) ||
      /single/i.test(packageSize) ||
      APPRAREL_SIZES.has(packageSize));

  /* eslint-disable jsx-a11y/no-redundant-roles */
  return productUrl ? (
    <li
      ref={cardRef}
      className={`p-0 ${customCss}`}
      role="listitem"
      data-value="shop-card"
    >
      <div className="d-flex flex-column h-100">
        <div className={`mw-100 position-relative ${styles.image_container}`}>
          <EffectBadge badgeImageUrl={badgeImage?.url} effect={effect} />
          {!isAbbreviatedCard && (
            <div
              className={classNames(
                'position-absolute',
                styles.badge_container_top_right
              )}
            >
              {showDiscount && (
                <PillBadge id="discount-badge">
                  {`${discountPercent}% off`}
                </PillBadge>
              )}
              {storeSelection && inventoryStatus === 'NEW' && (
                <PillBadge
                  customCss="bg-secondary text-primary text-uppercase"
                  id="new-product-badge"
                >
                  {newProductText}
                </PillBadge>
              )}
              {storeSelection && inventoryStatus === 'BACK IN STOCK' && (
                <PillBadge
                  customCss="bg-secondary text-primary text-uppercase"
                  id="back-in-stock-badge"
                >
                  {backInStockText}
                </PillBadge>
              )}
              {storeSelection && tags?.includes('Closeout') && (
                <PillBadge
                  customCss="bg-red text-white text-uppercase"
                  id="closeout-badge"
                >
                  {closeoutText}
                </PillBadge>
              )}
              {storeSelection &&
                tags?.includes('Featured Product') &&
                inventoryStatus !== 'BACK IN STOCK' &&
                inventoryStatus !== 'NEW' && (
                  <PillBadge id="featured-product-badge">
                    {featuredProductText}
                  </PillBadge>
                )}
              {promo && (
                <PillBadge
                  customCss={`bg-primary text-white text-${
                    promo?.textTransform || 'uppercase'
                  }`}
                  id="promo-badge"
                >
                  {promo?.label}
                </PillBadge>
              )}
              {storeSelection && tags?.includes('Price Drop') && (
                <PillBadge
                  customCss="bg-primary text-white text-uppercase"
                  id="price-drop-badge"
                >
                  {priceDropText}
                </PillBadge>
              )}
            </div>
          )}
          <div
            className={`h-100 w-100 position-absolute ${styles.link_wrapper}`}
          >
            {isUpsell ? (
              <Image
                fill
                legacy
                alt={displayTitle}
                blurDataURL={placeholder}
                className="mw-100"
                errorFallback={errorFallbackImage}
                objectFit="contain"
                placeholder={placeholder ? 'blur' : 'empty'}
                quality={70}
                sizes="(max-width: 992px) 50vw, 25vw"
                src={primaryImageUrl}
              />
            ) : (
              <Link
                href={productUrl}
                aria-label={`${displayTitle}${
                  discountPercent ? `. ${discountPercent}% Off` : ''
                }${effect ? `. Effect: ${effect}` : ''}`}
                className={`d-inline-block h-100 position-relative w-100 ${styles.featured_image}`}
                title={displayTitle}
              >
                <Image
                  fill
                  legacy
                  alt={displayTitle}
                  blurDataURL={placeholder}
                  className="mw-100"
                  errorFallback={errorFallbackImage}
                  objectFit="contain"
                  placeholder={placeholder ? 'blur' : 'empty'}
                  quality={70}
                  sizes="(max-width: 992px) 50vw, 25vw"
                  src={primaryImageUrl}
                />
              </Link>
            )}
          </div>
        </div>
        <div
          className={classNames('d-flex flex-column mb-4', styles.break_word)}
        >
          {isUpsell ? (
            <h3 className="fs-8 fs-xl-10 font-roboto fw-700 py-4 text-uppercase text-primary">
              {displayTitle}
            </h3>
          ) : (
            <Link href={productUrl} data-value="product-url">
              <h3 className="fs-8 fs-xl-10 font-roboto fw-700 py-4 text-uppercase text-primary">
                {displayTitle}
              </h3>
            </Link>
          )}
          <ul className="list-unstyled fs-7 fs-xl-8" data-value="product-info">
            {displayVariation ? (
              <li>
                <strong>
                  {transformVariation(displayVariation, category)}
                </strong>
              </li>
            ) : usePackageSize ? (
              <li>
                <strong>
                  {Number.isFinite(packageSize)
                    ? `${packageSize}-Pack`
                    : packageSize.replace(/\(/g, '').replace(/\)/g, '')}
                </strong>
              </li>
            ) : weight &&
              weightUOM &&
              !['EDIBLES', 'APPAREL', 'ACCESSORIES'].includes(
                category?.toUpperCase()
              ) ? (
              <li>
                <strong>{`${weight} ${weightUOM}`}</strong>
              </li>
            ) : null}
            {thcCbdTerps ? (
              <li>
                <span className="d-flex flex-row align-items-center">
                  {thcCbdTerps}
                  {showTerpeneInfo && totalTerpenes && terpeneTooltipInfo ? (
                    <div className="ps-2">
                      <LumeTooltip
                        iconColorClass="text-black"
                        tooltipPlacement="top"
                      >
                        <span>{terpeneTooltipInfo}</span>
                      </LumeTooltip>
                    </div>
                  ) : null}
                </span>
              </li>
            ) : null}
            {showPackagedDate && packagedDate ? (
              <li>
                <span className="d-flex flex-row align-items-center">
                  {`${packagedDateLabel}: ${formattedPackagedDate}`}
                  {packagedDateTooltipInfo ? (
                    <div className="ps-2">
                      <LumeTooltip
                        iconColorClass="text-black"
                        tooltipPlacement="top"
                      >
                        <span>{packagedDateTooltipInfo}</span>
                      </LumeTooltip>
                    </div>
                  ) : null}
                </span>
              </li>
            ) : null}
            {strain ? (
              <li>
                <span>{strain}</span>
              </li>
            ) : null}
            {
              // Since brand is needed to add to bag, we have to use upsell flag to hide this element
              !isAbbreviatedCard && !isUpsell && brand ? (
                <li>
                  <span data-value="product-brand">{brand}</span>
                </li>
              ) : null
            }
          </ul>
        </div>
        <div className="d-flex flex-column mt-auto">
          <ProductDiscountsInfo
            containerCustomStyling="mb-2"
            discounts={availableDiscounts}
            groupedDiscountsContent={groupedDiscountsContent}
          />
          <div className="d-flex flex-row">
            {storeSelection && !outOfStock && (
              <div className="d-flex flex-row font-sofia fs-10 fw-700 w-100">
                <span
                  className="d-flex flex-row text-primary"
                  data-value="product-price"
                  aria-label={`$${dollars}.${cents}`}
                >
                  <span>{`$${dollars}`}</span>
                  <span
                    className={`d-inline-block fs-7 ${styles.cents}`}
                  >{`.${cents}`}</span>
                </span>
                {fullDollars ? (
                  <span
                    className="d-flex flex-row fw-500 ms-4 mt-1 text-muted"
                    data-value="product-full-price"
                    aria-label={`Price was $${fullDollars}.${fullCents}`}
                  >
                    <span
                      className={classNames(
                        'fs-8 ps-1',
                        styles.strikethrough_dollars
                      )}
                    >{`$${fullDollars}`}</span>
                    <span
                      className={`d-inline-block fs-6 ${styles.full_cents}`}
                    >{`.${fullCents}`}</span>
                  </span>
                ) : null}
              </div>
            )}
            <div className={styles.heart_container}>
              <FavoriteProductButton
                className={styles.heart_icon}
                isFavorite={favoriteSkus?.includes(SKU)}
                handleButtonClick={handleToggleFavorite}
              />
            </div>
          </div>
          {showFavoriteError && (
            <span className="text-red fw-600">{FAVORITE_ERROR_MESSAGE}</span>
          )}
          <AddToBagButton
            availableQuantity={availableQuantity}
            batchSKU={batchSKU}
            brand={brand}
            cartQuantity={cartQuantity}
            category={category}
            className="mt-5 w-100"
            currencyCode={currencyCode}
            eventType={isUpsell ? ADD_TO_CART_UPSELL : ADD_TO_CART}
            outOfStock={outOfStock}
            price={price}
            productName={displayTitle}
            SKU={`${SKU}${itemSuffix}`}
            storeSelection={storeSelection}
            store={store}
            storeClosed={storeClosed}
            variation={variation}
          />
        </div>
      </div>
    </li>
  ) : null;
}
